'use client';

import {
  DialogHTMLAttributes,
  KeyboardEvent,
  ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
  forwardRef,
  ForwardedRef,
  useImperativeHandle,
  createElement,
} from 'react';
import classNames from 'classnames';

import { isClient } from '../../utils';
import { registerFocusRestoreDialog } from './utils/registerFocusRestoreDialog';
import { Button } from '../..';
import './css/Modal.scss';

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

const HEADING_ELEMENT = {
  1: 'h1',
  2: 'h2',
  3: 'h3',
  4: 'h4',
  5: 'h5',
  6: 'h6',
};

export interface ModalProps extends DialogHTMLAttributes<HTMLDialogElement> {
  children: ReactNode;
  closeButtonText?: string;
  disableESCKeyClose?: boolean;
  hasCloseButton?: boolean;
  heading: ReactNode;
  headingLevel?: HeadingLevel;
  isOpen: boolean;
  onAfterClose?: () => void;
  onAfterOpen?: () => void;
  onClose?: () => void;
  renderIcon?: ReactNode;
  role?: 'dialog' | 'alertdialog';
  size?: 'sm' | 'md' | 'lg'; // DEPRECATED: remove size 'md' in v4 release
}

const CloseIcon = (
  <svg
    aria-hidden="true"
    focusable="false"
    width="22"
    height="22"
    viewBox="0 0 22 22"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M14.3873 7L11 10.3786L7.61269 7L7 7.61269L10.3786 11L7 14.3873L7.61269 15L11 11.6214L14.3873 15L15 14.3873L11.6214 11L15 7.61269L14.3873 7V7Z"
      fill="black"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0 11C0 4.92487 4.92487 0 11 0C13.9174 0 16.7153 1.15893 18.7782 3.22183C20.8411 5.28473 22 8.08262 22 11C22 17.0751 17.0751 22 11 22C4.92487 22 0 17.0751 0 11ZM0.916667 11C0.916667 16.5689 5.43113 21.0833 11 21.0833C13.6743 21.0833 16.239 20.021 18.13 18.13C20.021 16.239 21.0833 13.6743 21.0833 11C21.0833 5.43113 16.5689 0.916667 11 0.916667C5.43113 0.916667 0.916667 5.43113 0.916667 11Z"
      fill="black"
    />
  </svg>
);

const hasDialogSupport = () =>
  typeof window !== 'undefined' && !!window.HTMLDialogElement;

export const Modal = forwardRef(
  (
    {
      children,
      className,
      closeButtonText = 'Close',
      disableESCKeyClose,
      hasCloseButton = true,
      heading,
      headingLevel = 2,
      isOpen,
      onAfterClose,
      onAfterOpen,
      onClose,
      renderIcon,
      role = 'dialog',
      size = 'lg',
      ...spreadProps
    }: ModalProps,
    outerRef: ForwardedRef<HTMLDialogElement>
  ) => {
    const [isDialogReady, setIsDialogReady] = useState(hasDialogSupport());

    const modalRef = useRef<HTMLDialogElement>(null);
    const headingRef = useRef<HTMLHeadingElement>(null);
    const baseId = useId();
    const headingId = `${baseId}-heading`;
    const iconId = `${baseId}-icon`;
    const modalLabelIds = renderIcon ? `${iconId} ${headingId}` : headingId;

    useImperativeHandle(outerRef, () => modalRef.current!, []);

    const handleCloseModal = () => {
      if (onClose) {
        onClose();
      }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDialogElement>) => {
      if (event.key === 'Escape') {
        if (disableESCKeyClose) {
          event.preventDefault();
        } else {
          handleCloseModal();
        }
      }
    };

    const loadPolyfill = () => {
      if (hasDialogSupport()) return Promise.resolve();
      return import('dialog-polyfill');
    };

    /**
     * Checks for overflow & adds tabindex="0" to the scrollable region.
     * Ensures scrollable region is focusable for keyboard only users.
     */
    const setScrollableRegionTabIndex = (
      modalElement: HTMLDialogElement | null
    ) => {
      if (!modalElement) return;

      const scrollableRegion = modalElement.querySelector(
        '.ps-modal-wrapper'
      ) as HTMLDivElement;

      if (
        scrollableRegion &&
        scrollableRegion.scrollHeight > scrollableRegion.clientHeight
      ) {
        scrollableRegion.setAttribute('tabindex', '0');
      } else {
        if (scrollableRegion.getAttribute('tabindex')) {
          scrollableRegion.removeAttribute('tabindex');
        }
      }
    };

    useEffect(() => {
      const modalElement = modalRef.current;

      // Timeout to debounce the resize event
      let resizeTimeout: NodeJS.Timeout;

      const handleResize = () => {
        // Clear the previous timeout
        clearTimeout(resizeTimeout!);
        // Debouncing the resize event to improve performance and avoid excessive calls
        resizeTimeout = setTimeout(() => {
          setScrollableRegionTabIndex(modalElement);
        }, 200);
      };

      if (modalElement) {
        if (isOpen && headingRef.current) {
          modalElement.showModal();
          // Use setTimeout() to ensure focus is set after the dialog is open, mainly for iOS VO + Safari
          setTimeout(() => headingRef.current?.focus(), 0);
          if (onAfterOpen) onAfterOpen();

          // Add window resize event listener to set the tabindex on the scrollable region
          window.addEventListener('resize', handleResize);

          // Set the initial tabindex on the scrollable region
          handleResize();
        } else if (isDialogReady) {
          modalElement.close();
          if (onAfterClose) onAfterClose();
        }
      }

      // Cleanup the resize event listener
      return () => {
        window.removeEventListener('resize', handleResize);
      };
      // TODO: React Hook useEffect has missing dependencies: 'isDialogReady', 'onAfterClose', and 'onAfterOpen'. Either include them or remove the dependency array. If 'onAfterOpen' changes too often, find the parent component that defines it and wrap that definition in useCallback.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    /**
     * Conditionally load polyfill
     * Inspired by https://github.com/woofers/react-dialog-polyfill/blob/master/src/index.js
     */
    useEffect(() => {
      const modalElement = modalRef.current;

      if (isDialogReady || !modalElement) return;

      let isSubscribed = true;

      loadPolyfill()
        .then((polyfill) => {
          if (!polyfill) return;
          polyfill.default.registerDialog(modalElement);
          registerFocusRestoreDialog(modalElement);
        })
        .catch((err) => {
          console.warn(`dialog-polyfill could not be loaded`, err);
        })
        .finally(() => {
          if (isSubscribed) setIsDialogReady(true);
        });

      // Prevent state update from firing after component unmounted
      return () => {
        isSubscribed = false;
        setIsDialogReady(false);
      };
      // TODO: React Hook useEffect has a missing dependency: 'isDialogReady'. Either include it or remove the dependency array
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // invert logic to support deprecated size 'md'
    // this can be updated to `size === 'lg'` in v4 release
    const isLarge = size !== 'sm';

    const modalClasses = classNames({
      'ps-modal': true,
      'ps-modal-lg': isLarge,
      'ps-modal-is-polyfilled': isClient() && !hasDialogSupport(),
      'ps-modal-has-icon': renderIcon && isLarge,
      [`${className}`]: className,
    });

    /**
     * The Dialog Element has a cancel event that is called after the ESC key is pressed.
     * The KeyDown Event is doing this for us by invoking the same HTMLDialogElement.close() method,
     * giving us the same functionality while making state management easy.
     *
     * However, the cancel event is still happening in the background, so by calling
     * Event.preventDefault() it both stops a redundant close() method from being called
     * as well as allowing us to clearly disable the ESC key from closing the Modal for components
     * like the AlertModal.
     */
    const preventDefaultESCKeyClose = (
      e: React.SyntheticEvent<HTMLDialogElement, Event>
    ) => e.preventDefault();

    return (
      <dialog
        {...spreadProps}
        aria-labelledby={modalLabelIds}
        aria-modal={true}
        className={modalClasses}
        onCancel={preventDefaultESCKeyClose}
        onKeyDown={handleKeyDown}
        ref={modalRef}
        role={role}
      >
        <div>
          {hasCloseButton && (
            <div className="ps-modal-above-content">
              <Button
                onClick={handleCloseModal}
                renderTrailingIcon={CloseIcon}
                size="sm"
                subtype="reversed"
                variant="text"
                className={
                  closeButtonText !== 'Close'
                    ? 'ps-modal-no-text-transform'
                    : undefined
                }
              >
                {closeButtonText}
              </Button>
            </div>
          )}
          <div className="ps-modal-wrapper">
            {renderIcon && (
              <div
                className="ps-modal-icon"
                id={iconId}
              >
                {renderIcon}
              </div>
            )}
            <div className="ps-modal-content">
              {
                /**
                 * CreatElement() inline so headingRef is available to focus during useEffect().
                 * Abstracting it out won't affect Modal, however AlertModal would lose the ability
                 * to focus on the heading due to the forwarded refs not being available.
                 */
                createElement(
                  HEADING_ELEMENT[headingLevel] ?? 'h2',
                  {
                    className: 'ps-modal-content-heading',
                    id: headingId,
                    ref: headingRef,
                    tabIndex: -1,
                  },
                  heading
                )
              }
              {children}
            </div>
          </div>
        </div>
      </dialog>
    );
  }
);
